/**
 * Date Utility Functions
 * Centralized date formatting and manipulation utilities for USA format
 * All time displays use 12-hour format with AM/PM (never 24-hour format)
 */

import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { DATE_FORMATS } from '../constants';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);

const CALENDAR_DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const extractCalendarDateKey = (value: string): string | null => {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

/**
 * Parse a calendar date (e.g. DOB) without timezone shifting.
 */
export const parseCalendarDate = (date?: string | Date | Dayjs | null): Dayjs | null => {
  if (!date) return null;
  if (dayjs.isDayjs(date)) return date;

  if (date instanceof Date) {
    const dateKey = extractCalendarDateKey(date.toISOString());
    return dateKey ? dayjs.utc(dateKey) : null;
  }

  const raw = String(date).trim();
  if (!raw) return null;

  if (CALENDAR_DATE_KEY_PATTERN.test(raw)) {
    return dayjs.utc(raw);
  }

  const dateKey = extractCalendarDateKey(raw);
  if (dateKey) {
    return dayjs.utc(dateKey);
  }

  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
};

/**
 * Format a calendar date (e.g. DOB) without timezone shifting.
 */
export const formatDateOnly = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  const parsed = parseCalendarDate(date);
  return parsed?.isValid() ? parsed.format(DATE_FORMATS.DISPLAY) : fallback;
};

/**
 * Convert a stored calendar date into a local Dayjs value for date pickers.
 */
export const toDatePickerValue = (date?: string | Date | Dayjs | null): Dayjs | null => {
  const dateKey = parseCalendarDate(date)?.format(DATE_FORMATS.API);
  if (!dateKey) return null;
  const pickerValue = dayjs(dateKey, DATE_FORMATS.API);
  return pickerValue.isValid() ? pickerValue : null;
};

/**
 * Format a date to USA short format (MM/DD/YYYY)
 * @example "01/29/2026"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MM/DD/YYYY format
 */
export const formatDate = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY) : fallback;
};

/**
 * Format a date to USA short format (alias for formatDate)
 * @example "01/29/2026"
 */
export const formatShortDate = formatDate;

/**
 * Format a date to USA long format (MMMM DD, YYYY)
 * @example "January 29, 2026"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MMMM DD, YYYY format
 */
export const formatLongDate = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY_FULL) : fallback;
};

/**
 * Format a date to USA medium format (MMM DD, YYYY)
 * @example "Jan 29, 2026"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MMM DD, YYYY format
 */
export const formatMediumDate = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY_VERBOSE) : fallback;
};

/**
 * Format a date to verbose USA format (MMM DD, YYYY)
 * @example "Jan 29, 2026"
 * @deprecated Use formatMediumDate instead for clarity
 */
export const formatDateVerbose = formatMediumDate;

/**
 * Format time only in 12-hour format with AM/PM
 * @example "2:30 PM"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted time string in h:mm A format
 */
export const formatTime = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.TIME_ONLY) : fallback;
};

/**
 * Format time with seconds in 12-hour format with AM/PM
 * @example "2:30:45 PM"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted time string in h:mm:ss A format
 */
export const formatTimeWithSeconds = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.TIME_ONLY_WITH_SECONDS) : fallback;
};

/**
 * Format a date with time to USA format (MM/DD/YYYY h:mm A)
 * @example "01/29/2026 2:30 PM"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MM/DD/YYYY h:mm A format (12-hour)
 */
export const formatDateTime = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY_WITH_TIME) : fallback;
};

/**
 * Format a date with time to verbose USA format (MMM DD, YYYY h:mm A)
 * @example "Jan 29, 2026 2:30 PM"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MMM DD, YYYY h:mm A format (12-hour)
 */
export const formatDateTimeVerbose = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY_VERBOSE_WITH_TIME) : fallback;
};

/**
 * Format a date with time to long USA format (MMMM DD, YYYY at h:mm A)
 * @example "January 29, 2026 at 2:30 PM"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MMMM DD, YYYY at h:mm A format (12-hour)
 */
export const formatDateTimeLong = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY_FULL_WITH_TIME) : fallback;
};

/**
 * Format a date for API submission (YYYY-MM-DD)
 * @param date - Date string, Date object, or Dayjs object
 * @returns Formatted date string in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateForApi = (date?: string | Date | Dayjs | null): string => {
  if (!date) return '';
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.API) : '';
};

/**
 * Format a date with time for API submission (YYYY-MM-DD HH:mm:ss)
 * @param date - Date string, Date object, or Dayjs object
 * @returns Formatted date string in YYYY-MM-DD HH:mm:ss format or empty string if invalid
 */
export const formatDateTimeForApi = (date?: string | Date | Dayjs | null): string => {
  if (!date) return '';
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.API_WITH_TIME) : '';
};

/**
 * Format month and year (MM/YYYY)
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Formatted date string in MM/YYYY format
 */
export const formatMonthYear = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.DISPLAY_MONTH) : fallback;
};

/**
 * Calculate age from date of birth
 * @param dateOfBirth - Date of birth string, Date object, or Dayjs object
 * @returns Age in years or null if invalid
 */
export const calculateAge = (dateOfBirth?: string | Date | Dayjs | null): number | null => {
  if (!dateOfBirth) return null;
  const dob = dayjs(dateOfBirth);
  if (!dob.isValid()) return null;
  return dayjs().diff(dob, 'year');
};

/**
 * Check if a date is in the past
 * @param date - Date string, Date object, or Dayjs object
 * @returns True if date is in the past, false otherwise
 */
export const isPastDate = (date?: string | Date | Dayjs | null): boolean => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() && dayjsDate.isBefore(dayjs(), 'day');
};

/**
 * Check if a date is in the future
 * @param date - Date string, Date object, or Dayjs object
 * @returns True if date is in the future, false otherwise
 */
export const isFutureDate = (date?: string | Date | Dayjs | null): boolean => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() && dayjsDate.isAfter(dayjs(), 'day');
};

/**
 * Check if a date is today
 * @param date - Date string, Date object, or Dayjs object
 * @returns True if date is today, false otherwise
 */
export const isToday = (date?: string | Date | Dayjs | null): boolean => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() && dayjsDate.isSame(dayjs(), 'day');
};

/**
 * Check if a date is yesterday
 * @param date - Date string, Date object, or Dayjs object
 * @returns True if date is yesterday, false otherwise
 */
export const isYesterday = (date?: string | Date | Dayjs | null): boolean => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  const yesterday = dayjs().subtract(1, 'day');
  return dayjsDate.isValid() && dayjsDate.isSame(yesterday, 'day');
};

/**
 * Check if a date is tomorrow
 * @param date - Date string, Date object, or Dayjs object
 * @returns True if date is tomorrow, false otherwise
 */
export const isTomorrow = (date?: string | Date | Dayjs | null): boolean => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  const tomorrow = dayjs().add(1, 'day');
  return dayjsDate.isValid() && dayjsDate.isSame(tomorrow, 'day');
};

/**
 * Format time range in 12-hour format (h:mm A - h:mm A)
 * @example "9:00 AM - 5:00 PM"
 * @param startDate - Start time
 * @param endDate - End time
 * @param fallback - Fallback string if invalid
 * @returns Formatted time range string
 */
export const formatTimeRange = (
  startDate?: string | Date | Dayjs | null,
  endDate?: string | Date | Dayjs | null,
  fallback: string = '—'
): string => {
  if (!startDate || !endDate) return fallback;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return fallback;
  return `${start.format(DATE_FORMATS.TIME_ONLY)} - ${end.format(DATE_FORMATS.TIME_ONLY)}`;
};

/**
 * Format relative time with smart logic
 * @example "Just now", "5 minutes ago", "Yesterday at 2:30 PM", "Jan 29, 2026"
 * @param date - Date string, Date object, or Dayjs object
 * @param fallback - Fallback string if date is invalid (default: '—')
 * @returns Relative time string or formatted date
 */
export const formatRelativeTime = (date?: string | Date | Dayjs | null, fallback: string = '—'): string => {
  if (!date) return fallback;
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return fallback;
  
  const now = dayjs();
  const diffInSeconds = Math.abs(now.diff(dayjsDate, 'second'));
  const diffInMinutes = Math.abs(now.diff(dayjsDate, 'minute'));
  const diffInHours = Math.abs(now.diff(dayjsDate, 'hour'));
  const diffInDays = Math.abs(now.diff(dayjsDate, 'day'));
  const isPast = dayjsDate.isBefore(now);
  
  // Just now (< 1 minute)
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  // Minutes ago/from now (< 1 hour)
  if (diffInMinutes < 60) {
    return isPast ? `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago` : `In ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
  }
  
  // Hours ago/from now (< 24 hours)
  if (diffInHours < 24) {
    return isPast ? `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago` : `In ${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
  }
  
  // Today/Yesterday/Tomorrow with time
  if (isToday(dayjsDate)) {
    return `Today at ${formatTime(dayjsDate)}`;
  }
  if (isYesterday(dayjsDate)) {
    return `Yesterday at ${formatTime(dayjsDate)}`;
  }
  if (isTomorrow(dayjsDate)) {
    return `Tomorrow at ${formatTime(dayjsDate)}`;
  }
  
  // Within last/next 7 days
  if (diffInDays < 7) {
    return isPast 
      ? `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`
      : `In ${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
  }
  
  // Older/further - show medium date
  return formatMediumDate(dayjsDate);
};

/**
 * Get relative time from now (alias for formatRelativeTime)
 * @deprecated Use formatRelativeTime instead for clarity
 */
export const getRelativeTime = formatRelativeTime;

/**
 * Get days until a future date (positive) or days since a past date (negative)
 * @param date - Date string, Date object, or Dayjs object
 * @returns Number of days or null if invalid
 */
export const getDaysUntil = (date?: string | Date | Dayjs | null): number | null => {
  if (!date) return null;
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return null;
  return dayjsDate.diff(dayjs(), 'day');
};

/**
 * Format a date range
 * @param startDate - Start date
 * @param endDate - End date
 * @param separator - Separator between dates (default: ' - ')
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate?: string | Date | Dayjs | null,
  endDate?: string | Date | Dayjs | null,
  separator: string = ' - '
): string => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (start === '—' && end === '—') return '—';
  if (start === '—') return end;
  if (end === '—') return start;
  return `${start}${separator}${end}`;
};

/**
 * Check if a credential/license is expiring soon (within 30 days)
 * @param expirationDate - Expiration date
 * @param warningDays - Number of days before expiration to warn (default: 30)
 * @returns True if expiring soon, false otherwise
 */
export const isExpiringSoon = (
  expirationDate?: string | Date | Dayjs | null,
  warningDays: number = 30
): boolean => {
  if (!expirationDate) return false;
  const daysUntil = getDaysUntil(expirationDate);
  return daysUntil !== null && daysUntil >= 0 && daysUntil <= warningDays;
};

/**
 * Check if a credential/license is expired
 * @param expirationDate - Expiration date
 * @returns True if expired, false otherwise
 */
export const isExpired = (expirationDate?: string | Date | Dayjs | null): boolean => {
  return isPastDate(expirationDate);
};

/**
 * Format date for HTML input fields (YYYY-MM-DD)
 * @param date - Date string, Date object, or Dayjs object
 * @returns Formatted date string in YYYY-MM-DD format or empty string if invalid
 */
export const formatForInput = (date?: string | Date | Dayjs | null): string => {
  if (!date) return '';
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format(DATE_FORMATS.API) : '';
};

/**
 * Parse a USA date string (MM/DD/YYYY) to Dayjs object
 * @param dateString - Date string in MM/DD/YYYY format
 * @returns Dayjs object or null if invalid
 */
export const parseUsaDate = (dateString?: string | null): Dayjs | null => {
  if (!dateString) return null;
  const parsed = dayjs(dateString, DATE_FORMATS.DISPLAY);
  return parsed.isValid() ? parsed : null;
};

/**
 * Get the start of today
 * @returns Dayjs object representing the start of today
 */
export const getStartOfToday = (): Dayjs => {
  return dayjs().startOf('day');
};

/**
 * Get the end of today
 * @returns Dayjs object representing the end of today
 */
export const getEndOfToday = (): Dayjs => {
  return dayjs().endOf('day');
};
